import type posthogJs from "posthog-js"

import { getPostHogPersistence, isChromeExtensionEmbeddedStandaloneWebphone, shouldForcePostHogSessionRecording, type PostHogRuntimeContext } from "@/lib/posthog/runtime"

const EMBEDDED_WEBPHONE_SHELL_INIT_EVENT = "embedded_webphone_shell_posthog_initialized"
const EMBEDDED_WEBPHONE_SHELL_SESSION_EVENT = "embedded_webphone_shell_posthog_session_ready"
const EMBEDDED_WEBPHONE_SHELL_RECORDER_EVENT = "embedded_webphone_shell_posthog_recorder_status"
const RECORDER_STATUS_DELAY_MS = 1500

interface EmbeddedStandaloneWebphoneDiagnostics {
  ancestorOriginCount: number
  hasChromeExtensionAncestor: boolean
  isFramed: boolean
  pathname: string
  persistence: "localStorage" | "localStorage+cookie"
  shouldForceRecording: boolean
}

function getSessionRecordingStarted(client: typeof posthogJs): boolean {
  return typeof client.sessionRecordingStarted === "function" ? client.sessionRecordingStarted() : false
}

function getSessionId(client: typeof posthogJs): string {
  return typeof client.get_session_id === "function" ? client.get_session_id() : ""
}

export function getEmbeddedStandaloneWebphoneDiagnostics(context: PostHogRuntimeContext): EmbeddedStandaloneWebphoneDiagnostics | null {
  if (!isChromeExtensionEmbeddedStandaloneWebphone(context)) {
    return null
  }

  return {
    pathname: context.pathname,
    isFramed: context.isFramed,
    ancestorOriginCount: context.ancestorOrigins.length,
    hasChromeExtensionAncestor: true,
    persistence: getPostHogPersistence(context),
    shouldForceRecording: shouldForcePostHogSessionRecording(context),
  }
}

export function registerEmbeddedStandaloneWebphoneDiagnostics(client: typeof posthogJs, context: PostHogRuntimeContext): () => void {
  const diagnostics = getEmbeddedStandaloneWebphoneDiagnostics(context)
  if (!diagnostics) {
    return () => {}
  }

  client.capture(EMBEDDED_WEBPHONE_SHELL_INIT_EVENT, {
    ...diagnostics,
    sessionIdPresent: Boolean(getSessionId(client)),
    sessionRecordingStarted: getSessionRecordingStarted(client),
  })

  let unsubscribeSessionId: (() => void) | undefined
  unsubscribeSessionId = client.onSessionId((sessionId, windowId) => {
    client.capture(EMBEDDED_WEBPHONE_SHELL_SESSION_EVENT, {
      ...diagnostics,
      sessionId,
      sessionIdPresent: Boolean(sessionId),
      sessionRecordingStarted: getSessionRecordingStarted(client),
      windowId,
    })

    unsubscribeSessionId?.()
    unsubscribeSessionId = undefined
  })

  const recorderStatusTimeout = window.setTimeout(() => {
    client.capture(EMBEDDED_WEBPHONE_SHELL_RECORDER_EVENT, {
      ...diagnostics,
      sessionIdPresent: Boolean(getSessionId(client)),
      sessionRecordingStarted: getSessionRecordingStarted(client),
    })
  }, RECORDER_STATUS_DELAY_MS)

  return () => {
    unsubscribeSessionId?.()
    window.clearTimeout(recorderStatusTimeout)
  }
}
